import React from 'react';
import { Sword, Heart, Package, Gem, TreePine, Bomb, Backpack } from 'lucide-react';

const ITEM_TYPE_ICONS = {
  0: { icon: Sword, color: '#FF4500', label: 'AR' },
  1: { icon: Heart, color: '#FF69B4', label: 'KT' },
  2: { icon: Backpack, color: '#FFA500', label: 'CM' },
  3: { icon: Package, color: '#87CEEB', label: 'OB' },
  4: { icon: Gem, color: '#9370DB', label: 'OE' },
  5: { icon: Gem, color: '#A9A9A9', label: 'MM' },
  6: { icon: TreePine, color: '#8B4513', label: 'MA' },
  7: { icon: Bomb, color: '#DC143C', label: 'EX' },
};

const ObjectPlacer = ({
  items = [],
  selectedItemId,
  setSelectedItemId,
  isBuried,
  setIsBuried,
  objectInstances = []
}) => {
  const selectedItem = items.find(i => i.id === selectedItemId);
  const typeInfo = ITEM_TYPE_ICONS[selectedItem?.item_type] || ITEM_TYPE_ICONS[3];
  const ItemIcon = typeInfo.icon;

  // Filtrar objetos por tipo para mejor organización
  const itemsByType = (type) => items.filter(i => i.item_type === type);

  return (
    <section className="space-y-4">
      <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
        <Package size={14} /> Objetos
      </h3>

      {/* Selector de objeto */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-slate-400">Seleccionar Objeto</label>
        <select
          value={selectedItemId || ''}
          onChange={(e) => setSelectedItemId(parseInt(e.target.value))}
          className="w-full bg-slate-900/50 border border-slate-700 rounded-xl p-3 text-white text-sm focus:border-game-primary focus:outline-none"
        >
          <option value="">-- Elegir objeto --</option>
          {items.map(item => {
            const typeInfo = ITEM_TYPE_ICONS[item.item_type] || ITEM_TYPE_ICONS[3];
            return (
              <option key={item.id} value={item.id}>
                [{typeInfo.label}] {item.name}
              </option>
            );
          })}
        </select>
      </div>

      {/* Info del objeto seleccionado */}
      {selectedItem && (
        <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700 space-y-3">
          <div className="flex items-center gap-3">
            {selectedItem.image && selectedItem.image !== 'default.png' ? (
              <img 
                src={`${import.meta.env.VITE_API_URL || ''}/uploads/gallery/${selectedItem.image}`} 
                alt={selectedItem.name} 
                className="w-12 h-12 rounded-lg object-cover border border-slate-600"
              />
            ) : (
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center border"
                style={{ 
                  backgroundColor: `${typeInfo.color}20`, 
                  borderColor: `${typeInfo.color}40` 
                }}
              >
                <ItemIcon size={24} style={{ color: typeInfo.color }} />
              </div>
            )}
            <div>
              <p className="text-sm text-white font-bold">{selectedItem.name}</p>
              <p className="text-xs font-semibold" style={{ color: typeInfo.color }}>
                [{typeInfo.label}] {Object.keys(ITEM_TYPE_ICONS).find(k => ITEM_TYPE_ICONS[k].label === typeInfo.label)}
              </p>
            </div>
          </div>

          {/* Stats del objeto */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {selectedItem.power > 0 && (
              <div className="text-slate-400">
                <span>Poder: </span>
                <span className="text-white font-bold">{selectedItem.power}</span>
              </div>
            )}
            {selectedItem.weight > 0 && (
              <div className="text-slate-400">
                <span>Peso: </span>
                <span className="text-white font-bold">{selectedItem.weight}</span>
              </div>
            )}
            {selectedItem.value > 0 && (
              <div className="text-slate-400">
                <span>Valor: </span>
                <span className="text-yellow-400 font-bold">{selectedItem.value} G</span>
              </div>
            )}
            {selectedItem.level_required > 0 && (
              <div className="text-slate-400">
                <span>Nivel: </span>
                <span className="text-blue-400 font-bold">Nv. {selectedItem.level_required}</span>
              </div>
            )}
          </div>

          {selectedItem.description && (
            <p className="text-[10px] text-slate-500 italic line-clamp-2">
              {selectedItem.description}
            </p>
          )}
        </div>
      )}

      {/* Checkbox Enterrado */}
      <div className="p-3 bg-amber-500/5 rounded-xl border border-amber-500/20">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isBuried}
            onChange={(e) => setIsBuried(e.target.checked)}
            className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
          />
          <div>
            <p className="text-sm text-white font-semibold">Objeto Enterrado</p>
            <p className="text-[10px] text-slate-500">
              Los objetos enterrados están ocultos y requieren acción especial para ser encontrados.
            </p>
          </div>
        </label>
      </div>

      {/* Lista de objetos en este sector */}
      {objectInstances.length > 0 && (
        <div className="space-y-2">
          <label className="text-xs font-semibold text-slate-400">Objetos en este Sector</label>
          <div className="max-h-40 overflow-y-auto space-y-1 custom-scrollbar">
            {objectInstances.slice(0, 10).map((instance, idx) => {
              const typeInfo = ITEM_TYPE_ICONS[instance.item_type] || ITEM_TYPE_ICONS[3];
              return (
                <div 
                  key={instance.id || idx} 
                  className="p-2 bg-slate-900/30 rounded-lg border border-slate-800 text-xs flex justify-between items-center"
                >
                  <div className="flex items-center gap-2">
                    <typeInfo.icon size={12} style={{ color: typeInfo.color }} />
                    <span className="text-slate-300">{instance.name || `Objeto #${instance.id_object || idx + 1}`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {instance.is_buried && <span className="text-amber-400 text-[10px]">🕳️</span>}
                    <span className="text-slate-500">Coord: {instance.coordinate}</span>
                  </div>
                </div>
              );
            })}
            {objectInstances.length > 10 && (
              <p className="text-[10px] text-slate-600 text-center">
                ...y {objectInstances.length - 10} más
              </p>
            )}
          </div>
        </div>
      )}

      {/* Instrucciones */}
      <div className="p-3 bg-emerald-500/5 rounded-xl border border-emerald-500/20">
        <p className="text-[10px] text-emerald-400">
          📦 Selecciona un objeto y haz clic en el mapa para colocarlo. Los objetos se pueden colocar en cualquier tile excepto agua profunda.
        </p>
      </div>
    </section>
  );
};

export default ObjectPlacer;
